import { useCallback, useEffect, useRef, useState } from "react";

type UseShippingPharmacyDraftProps = {
	confirmedValue?: string;
	onCommit: (vendorId: string) => void;
	isOpen: boolean;
};

type UseShippingPharmacyDraftReturn = {
	snapshot: string | undefined;
	draft: string | undefined;
	isDirty: boolean;
	onDraftSelect: (vendorId?: string) => void;
	onConfirm: () => void;
	onDismiss: () => void;
};

export function useShippingPharmacyDraft({
	confirmedValue,
	onCommit,
	isOpen,
}: UseShippingPharmacyDraftProps): UseShippingPharmacyDraftReturn {
	const snapshot = confirmedValue;
	const [draft, setDraft] = useState<string | undefined>(snapshot);
	// Figma 4597:88702: any explicit pick — even re-selecting the value that is
	// already applied, e.g. back to "alle Top-Apotheken" after a pharmacy —
	// enables the CTA, so dirtiness tracks interaction, not value difference.
	const [touched, setTouched] = useState(false);
	const prevIsOpen = useRef(isOpen);

	useEffect(() => {
		if (prevIsOpen.current !== isOpen) {
			setDraft(snapshot);
			setTouched(false);
		}
		prevIsOpen.current = isOpen;
	}, [isOpen, snapshot]);

	useEffect(() => {
		if (!isOpen) {
			setDraft(snapshot);
			setTouched(false);
		}
	}, [isOpen, snapshot]);

	const isDirty = touched && draft !== undefined;

	const onDraftSelect = useCallback((vendorId?: string) => {
		if (!vendorId) return;
		setDraft(vendorId);
		setTouched(true);
	}, []);

	const onConfirm = useCallback(() => {
		if (!draft || !touched) return;
		onCommit(draft);
	}, [draft, touched, onCommit]);

	const onDismiss = useCallback(() => {
		setDraft(snapshot);
		setTouched(false);
	}, [snapshot]);

	return {
		snapshot,
		draft,
		isDirty,
		onDraftSelect,
		onConfirm,
		onDismiss,
	};
}
